import { HttpClient } from '@wix/http-client';
import { v4 as uuid } from 'uuid';
import { CHAT_DEF_ID } from '../constants';
import { mediaTypesTranslation, mediaProviders } from '@wix/inbox-common';
import { UploadedFileResponse } from '../types';

const prefixChatPayments = '/_api/chat-payments/api';
const prefixPcf = '/serverless/pre-chat-form';
const prefixCrmInbox = '/_api/crm-inbox-server/chat';
const widgetRenderPrefix = '/chat-widget-server/renderChatWidget';
const prefixReportChatWidgetOpen = '/serverless/chat-widget-service';
const chatEventReporterPrefix = '/serverless/chat-event-reporter';

const PAYMENT_SUMMARY = `${prefixChatPayments}/get-payment-summary`;
const PCF_POST_FORM_URL = `${prefixPcf}/v1/pcf/submissions`;
const UPLOAD_TOKEN_URL = `${prefixCrmInbox}/uploadToken`;
const IMAGE_PREVIEW_PAGE = `${widgetRenderPrefix}/imagePreview`;
const REPORT_CHAT_WIDGET_OPEN_URL = `${prefixReportChatWidgetOpen}/chat-widget/v1/open`;
const VISITOR_ON_PAGE_URL = `${chatEventReporterPrefix}/report-event`;

export const ENDPOINTS = {
  PAYMENT_SUMMARY,
  PCF_POST_FORM_URL,
  UPLOAD_TOKEN_URL,
  IMAGE_PREVIEW_PAGE,
  REPORT_CHAT_WIDGET_OPEN_URL,
  VISITOR_ON_PAGE_URL,
};

export class ServerApi {
  constructor(private readonly httpClient: HttpClient) {}

  reportChatWidgetOpen() {
    return this.httpClient
      .post(REPORT_CHAT_WIDGET_OPEN_URL)
      .then((res) => res.data);
  }

  reportVisitorOnPageEvent(
    pageId: string,
    visitorId: string,
    idempotencyKey: string,
    pageName: string,
  ) {
    return this.httpClient
      .post(VISITOR_ON_PAGE_URL, {
        eventIdentifier: {
          eventUniqueId: uuid(),
          eventType: 'chatVisitor/visitOnPage',
          sourceUniqueId: CHAT_DEF_ID,
        },
        eventUTCTime: new Date().toISOString(),
        detailedEventPayload: {
          pageId: {
            value: pageId,
          },
          visitorId: {
            value: visitorId,
          },
          pageName: {
            value: pageName,
          },
        },
        idempotencyKey,
      })
      .then((res) => res.data);
  }

  initChat({
    deviceType,
    correlationId,
    refresh,
    oldVisitorInstanceId,
    prevChatToken,
  }: {
    deviceType?: string;
    correlationId?: string;
    refresh?: boolean;
    oldVisitorInstanceId?: string;
    prevChatToken?: string;
  }) {
    return this.httpClient
      .post('/_api/crm-inbox-server/chat/init', undefined, {
        params: {
          backend: 'CHAT',
          deviceType,
          correlationId,
          refresh,
          oldVisitorInstanceId,
          prevChatToken,
        },
      })
      .then((res) => res.data);
  }

  getPaymentSummary(orderId: string) {
    return this.httpClient
      .get(PAYMENT_SUMMARY, {
        params: {
          orderId,
        },
      })
      .then((res) => {
        return res.data;
      });
  }

  postPCF(fields) {
    return this.httpClient
      .post(PCF_POST_FORM_URL, { submission: { fields } })
      .then((res) => res.data);
  }

  getUploadToken() {
    return this.httpClient.get(UPLOAD_TOKEN_URL).then((res) => res.data);
  }

  generateFileUploadUrl(fileName: string, mimeType: string) {
    return this.httpClient
      .post('/chat-widget-server/comm-channels-media/upload-url', {
        fileName,
        mimeType,
      })
      .then((res) => res.data);
  }

  async uploadFileToSecureUrl(
    uploadUrl: string,
    fileType: string,
    file: File,
  ): Promise<UploadedFileResponse> {
    const axios = require('axios');
    const fileBuffer = await file.arrayBuffer();
    const params = { filename: file.name };
    const headers = {
      'Content-Type': fileType,
    };

    const uploadResponse = await axios.put(uploadUrl, fileBuffer, {
      headers,
      params,
      withCredentials: false,
    });

    const uploadedFile = uploadResponse?.data?.file;

    const response: UploadedFileResponse = {
      name: file.name,
      mediaFileId: uploadedFile?.id || file.name,
      url: uploadedFile.thumbnailUrl || uploadedFile.url || uploadUrl,
      contentType: fileType,
      fileSize: file.size,
      mediaProvider: mediaProviders.WIX_MEDIA_PUBLIC,
    };

    if (fileType === mediaTypesTranslation.image) {
      Object.assign(response, {
        width: uploadedFile.width || 0,
        height: uploadedFile.height || 0,
      });
    }
    return response;
  }

  generateFileDownloadUrl(fileId?: string) {
    return this.httpClient
      .post('/chat-widget-server/comm-channels-media/download-url', {
        fileId,
      })
      .then((res) => res?.data);
  }
}
